import { useState } from "react";

import { serializeError } from "serialize-error";

import twitterIcon from "@assets/platform-icons/twitter-filled-icon.svg";
import { useUser } from "@components/user-context";
import { useFeatureFlags } from "@hooks/http";
import { HTTP_METHOD } from "@hooks/use-rest-api";
import { bugsnagClient } from "@internal/analytics/bugsnag";
import { ConnectionType } from "@north-beam/nb-client-side";
import { ConnectionCard } from "@pages/settings/connection-card";
import {
  ConnectionStatusType,
  type LoginComponentChildrenProps,
  type LoginComponentWithFrontendIntegrationProps,
} from "@pages/settings/connection-status-manager";
import { type CardProps2 } from "@pages/settings/core";
import { PrimaryButton } from "@shared/buttons";
import { extractError } from "@utils/index";
import { launchOAuthPopup } from "@utils/oauth2-handler";

import { type AccountMetadataWithDescription } from "../account-selector";

export function TwitterConnectionCard(props: CardProps2) {
  const { hasFeatureFlag } = useFeatureFlags();
  if (!hasFeatureFlag("enableTwitterIntegration")) {
    return null;
  }

  return (
    <ConnectionCard
      type={type}
      connectionTypeName="Twitter Ads"
      connectionCallToAction={`
        Integrate your Twitter Ads data to start receiving insights about
        your Twitter ad spend.
      `}
      logoUrl={twitterIcon}
      logoAltText="Twitter Ads Logo"
      supportMultipleAccounts="subAccounts"
      knowledgeBaseLink="https://docs.northbeam.io/docs/tracking-for-twitter-ads"
    >
      {({ setStatus, accounts }: LoginComponentChildrenProps) => (
        <TwitterLoginComponent
          setStatus={setStatus}
          accounts={accounts}
          integrationDetails={props.integrationDetails}
        />
      )}
    </ConnectionCard>
  );
}

function TwitterLoginComponent({
  setStatus,
}: LoginComponentWithFrontendIntegrationProps) {
  const { user } = useUser();
  const [isButtonDisabled, setButtonDisabled] = useState(false);

  const onConnectionClicked = async () => {
    setButtonDisabled(true);

    let oAuthUrl: string | null = null;
    try {
      oAuthUrl = await user.callApi<string>("/api/twitter/request-token", {
        method: HTTP_METHOD.GET,
      });
    } catch (error) {
      setButtonDisabled(false);
    }

    if (!oAuthUrl) {
      bugsnagClient.notify("No oAuthUrl returned for TwitterAds connection");
      setStatus({
        type: ConnectionStatusType.HAS_ERROR,
        errorMessage:
          "An error occurred while trying to connect to Twitter Ads. Please try again later.",
      });
      return;
    }

    const callbackParams = await launchOAuthPopup(oAuthUrl);
    const callbackParamsParsed = new URLSearchParams(callbackParams);

    const oAuthToken = callbackParamsParsed.get("oauth_token");
    const oAuthVerifier = callbackParamsParsed.get("oauth_verifier");

    if (!oAuthToken || !oAuthVerifier) {
      bugsnagClient.notify(
        "Missing oAuth token or verifier to finish Twitter integration",
      );
      setStatus({
        type: ConnectionStatusType.HAS_ERROR,
        errorMessage:
          "Twitter connection failed. Please try again and accept the permissions to connect your account.",
      });
      return;
    }

    try {
      const twitterConnectionResult = await user.callApi<{
        error: string;
        accounts: AccountMetadataWithDescription[];
      }>("/api/twitter/callback", {
        method: "post",
        data: {
          oAuthToken,
          oAuthVerifier,
        },
      });

      if (twitterConnectionResult.error) {
        setStatus({
          type: ConnectionStatusType.HAS_ERROR,
          errorMessage: `An error occurred: ${serializeError(
            extractError(twitterConnectionResult.error),
          )}`,
        });
      } else {
        setStatus({
          type: ConnectionStatusType.SELECTING_ACCOUNT,
          accountList: twitterConnectionResult.accounts,
        });
      }
    } catch (error) {
      setStatus({
        type: ConnectionStatusType.HAS_ERROR,
        errorMessage: `An error occurred: ${serializeError(
          extractError(error),
        )}`,
      });
    }

    setButtonDisabled(false);
  };

  return (
    <div className="mb-3">
      <div className="row">
        <div className="col-auto">
          <PrimaryButton
            disabled={isButtonDisabled}
            onClick={onConnectionClicked}
          >
            {isButtonDisabled ? "Please Wait..." : "CONNECT"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

const type = ConnectionType.integration_twitter;
